import MprcLoadable from "components/MprcLoadable";
import { FeedType, KnowledgeFeedOrder, PublicationType } from "generated/CoreApi_v1";
import { KnowledgeLayout } from "layouts/KnowledgeLayout";
import { WelcomeLayout } from "layouts/WelcomeLayout";
import React from "react";
import { Navigate, Route, Routes } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { FeedChannel } from "views/knowledge/feed/FeedChannel";
import { FeedChannelInvite } from "views/knowledge/feed/FeedChannel/FeedChannelInvite";
import { FeedContribution } from "views/knowledge/feed/FeedContribution";
import { FeedFavorite } from "views/knowledge/feed/FeedFavorite";
import { FeedHistory } from "views/knowledge/feed/FeedHistory";
import { FeedList } from "views/knowledge/feed/FeedList";
import { FeedMyLists } from "views/knowledge/feed/FeedMyLists";
import { FeedQuestionAndAnswers } from "views/knowledge/feed/FeedQuestionAndAnswers";
import { FeedSeeLater } from "views/knowledge/feed/FeedSeeLater";
import { FeedShortVideoExplorer } from "views/knowledge/feed/FeedShortVideoExplorer";
import { FeedStartup } from "views/knowledge/feed/FeedStartup";
import { FeedTopic } from "views/knowledge/feed/FeedTopic";
import { FeedUserProfile } from "views/knowledge/feed/FeedUserProfile";
import { ListPlayback, ListViewer } from "views/knowledge/publication/pages/List";
import { PollThreadViewer, PollViewer } from "views/knowledge/publication/pages/Poll";
import { PostThreadViewer, PostViewer } from "views/knowledge/publication/pages/Post";
import { QuestionViewer } from "views/knowledge/publication/pages/Question/QuestionViewer";
import { SelectionQuickFilter } from "views/knowledge/settings/Home/QuickFilter/SelectionQuickFilter";
import { ListShorts } from "views/knowledge/shorts/Home/ListShorts";
import { QuickAccessEdit } from "views/knowledge/subcriptions/Home/QuickAccess";

const FeedPublicationsAdmin = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'knowledge' */"views/knowledge/feed/FeedPublicationsAdmin")));
const FeedModeration = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'knowledge' */"views/knowledge/feed/FeedModeration")));
const StartFeedModeration = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'knowledge' */"views/knowledge/feed/FeedModeration/StartFeedModeration")));
const EditKnowledgeSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'knowledge' */"views/knowledge/settings/Home/Setting/EditKnowledgeSettings")));
const EditPromoteSettings = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'knowledge' */"views/knowledge/settings/Home/Promote/EditPromoteSettings")));
const EditAward = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'knowledge' */"views/knowledge/settings/Home/Award/EditAward")));
const ListChannels = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'knowledge' */"views/knowledge/channels/Home/ListChannels")));
const SettingsHome = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'knowledge' */"views/knowledge/settings")));
const SubscriptionSignup = MprcLoadable(React.lazy(() => import(/* webpackChunkName: 'knowledge' */"views/knowledge/subcriptions/Home/SubscriptionSignup")));

class Paths {
    static readonly FeedStartup = "FeedForYou";
    static readonly FeedContributions = "FeedContributions";

    static readonly FeedHistory = "FeedHistory";
    static readonly FeedTasks = "FeedTasks";
    static readonly FeedModeration = "FeedModeration";
    static readonly FeedShorts = "FeedShorts";
    static readonly FeedQuestionAndAnswers = "FeedQuestionAndAnswers";
    static readonly FeedFavorites = "FeedFavorites";
    static readonly FeedFavoritesPlayback = "FeedFavoritesPlayback";
    static readonly FeedSeeLater = "FeedSeeLater";
    static readonly FeedSeeLaterPlayback = "FeedSeeLaterPlayback";
    static readonly FeedMyLists = "FeedMyLists";
    static readonly FeedChannel = "FeedChannel";
    static readonly FeedTopic = "FeedTopic";
    static readonly FeedTopicTrainings = "FeedTopicTrainings";
    static readonly FeedUserProfile = "FeedUserProfile";
    static readonly FeedList = "FeedList";

    static readonly FeedCustomList = "FeedCustomList";
    static readonly FeedChannelInvite = "FeedChannelInvite";

    static readonly Subcriptions = "Subcriptions";
    static readonly QuickAccessEdit = "QuickAccessEdit";

    static readonly Management = "Management";
    static readonly ManagementChannel = Paths.Management + "/Channel";
    static readonly ManagementPublication = Paths.Management + "/Publication";
    static readonly ManageShortVideos = Paths.Management + "/ShortVideos";

    static readonly Settings = "Settings";
    static readonly SettingsQuickFilter = Paths.Settings + "/QuickFilter";
    static readonly SettingsPromote = Paths.Settings + "/Promote";
    static readonly SettingsAward = Paths.Settings + "/Award";
    static readonly SettingsBadge = Paths.Settings + "/Badge";
    static readonly SettingsConfiguration = Paths.Settings + "/Configuration";

    static readonly PollPublicationViewer = "/Poll";
    static readonly PollThreadPublicationViewer = "/PollThread";
    static readonly PostPublicationViewer = "/Post";
    static readonly PostThreadPublicationViewer = "/PostThread";
    static readonly ListPublicationViewer = "/List";
    static readonly ListPublicationInPlayback = "/ListPlayback";
    static readonly QuestionPublicationViewer = "/Question";
}

export class KnowledgePaths {
    static readonly Prefix = "/knowledge";
    static readonly FeedStartup = KnowledgePaths.Prefix;
    static readonly FeedHistory = KnowledgePaths.Prefix + "/" + Paths.FeedHistory;
    static readonly FeedTasks = KnowledgePaths.Prefix + "/" + Paths.FeedTasks;

    static readonly FeedContributions = KnowledgePaths.Prefix + "/" + Paths.FeedContributions;
    static readonly FeedMyContributions = KnowledgePaths.FeedContributions + "/Contributions";
    static readonly FeedMyAnswers = KnowledgePaths.FeedContributions + "/Answers";
    static readonly FeedMyDrafts = KnowledgePaths.FeedContributions + "/Drafts";

    static readonly FeedModeration = KnowledgePaths.Prefix + "/" + Paths.FeedModeration;
    static readonly FeedModerationPublications = KnowledgePaths.FeedModeration + "/Publications";
    static readonly FeedModerationSubscriptions = KnowledgePaths.FeedModeration + "/Subscriptions";
    static readonly FeedModerationReports = KnowledgePaths.FeedModeration + "/Reports";

    static readonly FeedShorts = KnowledgePaths.Prefix + "/" + Paths.FeedShorts;
    static readonly FeedQuestionAndAnswers = KnowledgePaths.Prefix + "/" + Paths.FeedQuestionAndAnswers;
    static readonly FeedFavorites = KnowledgePaths.Prefix + "/" + Paths.FeedFavorites;
    static readonly FeedSeeLater = KnowledgePaths.Prefix + "/" + Paths.FeedSeeLater;
    static readonly FeedMyLists = KnowledgePaths.Prefix + "/" + Paths.FeedMyLists;

    static readonly Subcriptions = KnowledgePaths.Prefix + "/" + Paths.Subcriptions;
    static readonly FeedChannelInvite = KnowledgePaths.Prefix + "/" + Paths.FeedChannelInvite;

    static readonly ManagementChannel = KnowledgePaths.Prefix + "/" + Paths.ManagementChannel;
    static readonly ManagementPublication = KnowledgePaths.Prefix + "/" + Paths.ManagementPublication;
    static readonly ManageShortVideos = KnowledgePaths.Prefix + "/" + Paths.ManageShortVideos;

    static readonly Settings = KnowledgePaths.Prefix + "/" + Paths.Settings;
    static readonly SettingsQuickFilter = KnowledgePaths.Prefix + "/" + Paths.SettingsQuickFilter;
    static readonly SettingsPromote = KnowledgePaths.Prefix + "/" + Paths.SettingsPromote;
    static readonly SettingsAward = KnowledgePaths.Prefix + "/" + Paths.SettingsAward;
    static readonly SettingsBadge = KnowledgePaths.Prefix + "/" + Paths.SettingsBadge;
    static readonly SettingsConfiguration = KnowledgePaths.Prefix + "/" + Paths.SettingsConfiguration;
    static readonly QuickAccessEdit = KnowledgePaths.Prefix + "/" + Paths.QuickAccessEdit;

    private static readonly PollPublicationViewer = KnowledgePaths.Prefix + "/Poll";
    private static readonly PollThreadPublicationViewer = KnowledgePaths.Prefix + "/PollThread";
    private static readonly PostPublicationViewer = KnowledgePaths.Prefix + "/Post";
    private static readonly PostThreadPublicationViewer = KnowledgePaths.Prefix + "/PostThread";

    static readonly QuestionPublicationViewer = KnowledgePaths.Prefix + "/Question";
    static readonly ListPublicationViewer = KnowledgePaths.Prefix + "/List";
    static readonly ListPublicationInPlayback = KnowledgePaths.Prefix + "/ListPlayback";

    static FeedChannelHome(id: string) { return KnowledgePaths.Prefix + "/" + Paths.FeedChannel + "/Home/" + id; }
    static FeedTopicHome(id: string) { return KnowledgePaths.Prefix + "/" + Paths.FeedTopic + "/Home/" + id; }
    static FeedListHome(id: string) { return KnowledgePaths.Prefix + "/" + Paths.FeedList + "/Home/" + id; }
    static FeedUserProfileHome(id: string) { return KnowledgePaths.Prefix + "/" + Paths.FeedUserProfile + "/Home/" + id; }

    static FeedChannel(id: string, order?: KnowledgeFeedOrder) { return KnowledgePaths.Prefix + "/" + Paths.FeedChannel + "/" + id + (order == null ? "" : "?o=" + KnowledgeFeedOrder[order]); }


    static FeedTopic(id: string, order?: KnowledgeFeedOrder) { return KnowledgePaths.Prefix + "/" + Paths.FeedTopic + "/" + id + (order == null ? "" : "?o=" + KnowledgeFeedOrder[order]); }
    static FeedTopicTrainings(id: string) { return KnowledgePaths.Prefix + "/" + Paths.FeedTopicTrainings + "/" + id; }

    static FeedUserProfile(id: string) { return KnowledgePaths.Prefix + "/" + Paths.FeedUserProfile + "/" + id; }
    static FeedList(id: string, parentPublicationId: string | null = null) { return KnowledgePaths.PublicationViewerResolver(id, PublicationType.List, parentPublicationId); }

    static PostViewer(id: string, parentPublicationId: string | null = null) { return KnowledgePaths.PublicationViewerResolver(id, PublicationType.Post, parentPublicationId); }
    static PollViewer(id: string, parentPublicationId: string | null = null) { return KnowledgePaths.PublicationViewerResolver(id, PublicationType.Poll, parentPublicationId); }
    static QuestionViewer(id: string) { return KnowledgePaths.PublicationViewerResolver(id, PublicationType.Question); }
    static AnswerViewer(id: string, questionId: string) { return KnowledgePaths.PublicationViewerResolver(id, PublicationType.Question, questionId); }
    static BuildEditBadge(id: string | null) { return `${KnowledgePaths.SettingsBadge}/${id ? `${id}` : "New"}`; }

    /**
     * Obtem a URL para visualização da publicação.
     * @param publicationId
     * @param type
     * @param parentPublicationId
     * @returns
     */
    static PublicationViewerResolver(publicationId: string, type: PublicationType, parentPublicationId: string | null = null): string {
        switch (type) {
            case PublicationType.Post:
                return (parentPublicationId == null ? KnowledgePaths.PostPublicationViewer : KnowledgePaths.PostThreadPublicationViewer) + '/' + publicationId;
            case PublicationType.Poll:
                return (parentPublicationId == null ? KnowledgePaths.PollPublicationViewer : KnowledgePaths.PollThreadPublicationViewer) + '/' + publicationId;
            case PublicationType.List:
                return KnowledgePaths.ListPublicationViewer + "/" + publicationId;
            case PublicationType.Question:
                return KnowledgePaths.QuestionPublicationViewer + "/" + publicationId;
            case PublicationType.Answer:
                if (parentPublicationId) return KnowledgePaths.QuestionPublicationViewer + "/" + parentPublicationId + "/" + publicationId;
                throw new Error("Trying get Answer's Url but no question id provided.");
        }
        throw new Error("Knowledge: publication's type cannot be resolved.");
    }
}

export const KnowledgeRouter = () => {
    return (
        <Routes>
            <Route element={<KnowledgeLayout />}>
                <Route path="" element={<FeedStartup />} />
                <Route path={Paths.FeedStartup} element={<FeedStartup />} />
                <Route path={Paths.FeedContributions}>
                    <Route index element={<FeedContribution selectedOption={FeedType.MyContributions} />} />
                    <Route path="Contributions" element={<FeedContribution selectedOption={FeedType.MyContributions} />} />
                    <Route path="Answers" element={<FeedContribution selectedOption={FeedType.MyAnswers} />} />
                    <Route path="Drafts" element={<FeedContribution selectedOption={FeedType.MyDraft} />} />
                </Route>
                <Route path={Paths.FeedHistory} element={<FeedHistory />} />
                <Route path={Paths.FeedFavorites} element={<FeedFavorite />} />
                <Route path={Paths.FeedSeeLater} element={<FeedSeeLater />} />
                <Route path={Paths.FeedMyLists} element={<FeedMyLists />} />
                <Route path={Paths.FeedShorts} element={<FeedShortVideoExplorer />} />
                <Route path={Paths.FeedQuestionAndAnswers} element={<FeedQuestionAndAnswers />} />
                <Route path={Paths.FeedModeration}>
                    <Route index element={<StartFeedModeration />} />
                    <Route path="Publications" element={<FeedModeration selectedOption={FeedType.ModeratePublications} />} />
                    <Route path="Reports" element={<FeedModeration selectedOption={FeedType.ModerateReports} />} />
                    <Route path="Subscriptions" element={<FeedModeration selectedOption={FeedType.ModerateChannelSubscriptions} />} />
                </Route>
                <Route path={Paths.Subcriptions} element={<SubscriptionSignup />} />
                <Route path={Paths.FeedChannelInvite} element={<FeedChannelInvite />} />

                <Route path={Paths.ManagementChannel} element={<ListChannels />} />
                <Route path={Paths.ManagementPublication} element={<FeedPublicationsAdmin />} />
                <Route path={Paths.ManageShortVideos} element={<ListShorts />} />

                <Route path={Paths.Settings} element={<SettingsHome />} />
                <Route path={Paths.SettingsQuickFilter} element={<SelectionQuickFilter />} />
                <Route path={Paths.SettingsPromote} element={<EditPromoteSettings />} />
                <Route path={Paths.SettingsConfiguration} element={<EditKnowledgeSettings />} />
                <Route path={Paths.SettingsAward} element={<EditAward tabName="award" />} />
                <Route path={Paths.SettingsBadge} element={<EditAward tabName="badge" />} />

                <Route path={Paths.QuickAccessEdit} element={<QuickAccessEdit />} />

                <Route path={Paths.FeedChannel}>
                    <Route path="Home">
                        <Route path=":id" element={<FeedChannel isHome={true} />} />
                    </Route>
                    <Route path=":id" element={<FeedChannel />} />
                </Route>

                <Route path={Paths.FeedTopic}>
                    <Route path="Home">
                        <Route path=":id" element={<FeedTopic isHome={true} initialMode="knowledge" />} />
                    </Route>
                    <Route path=":id" element={<FeedTopic initialMode="knowledge" />} />
                </Route>

                <Route path={Paths.FeedList}>
                    <Route path="Home">
                        <Route path=":id" element={<FeedList isHome={true} />} />
                    </Route>
                    <Route path=":id" element={<FeedList />} />
                </Route>

                <Route path={Paths.FeedTopicTrainings}>
                    <Route path=":id" element={<FeedTopic initialMode="lms" />} />
                </Route>

                <Route path={Paths.FeedUserProfile}>
                    <Route path="Home">
                        <Route path=":id" element={<FeedUserProfile isHome={true} isKnowledge={true} />} />
                    </Route>
                    <Route path=":id" element={<FeedUserProfile isKnowledge={true} />} />
                </Route>

                <Route path={Paths.FeedCustomList}>
                    <Route path=":id" element={<FeedStartup />} />
                </Route>
            </Route>

            <Route element={<WelcomeLayout />}>
                <Route path={Paths.PollPublicationViewer} >
                    <Route path=":id" element={<PollViewer />} />
                </Route>
                <Route path={Paths.PollThreadPublicationViewer} >
                    <Route path=":id" element={<PollThreadViewer />} />
                </Route>
                <Route path={Paths.PostPublicationViewer} >
                    <Route path=":id" element={<PostViewer />} />
                </Route>
                <Route path={Paths.PostThreadPublicationViewer} >
                    <Route path=":id" element={<PostThreadViewer />} />
                </Route>
                <Route path={Paths.ListPublicationViewer} >
                    <Route path=":id" element={<ListViewer />} />
                </Route>
                <Route path={Paths.ListPublicationInPlayback} >
                    <Route path=":id" element={<ListPlayback />} />
                </Route>
                <Route path={Paths.QuestionPublicationViewer} >
                    <Route path=":id" element={<QuestionViewer />} />
                    <Route path=":id/:answerId?" element={<QuestionViewer />} />
                </Route>
            </Route>

            <Route path="*" element={<Navigate to={RootPaths.PageNotFound} />} />
        </Routes>
    );
};
